import axios from "axios";
import qs from "qs";
import { saveAs } from "file-saver";

let defaultConfig = {
  debug: false, // 调试模式，每个请求会打印
  baseURL: "",
  timeout: 15 * 1000,
  headers: {
    "Content-Type": "application/json",
    "Source-Client": "2", // 1-移动端，2-PC端，小程序-3
  },
  responseType: "json",

  /**
   * HTTP响应状态码处理
   * @param status
   * @returns {boolean}
   */
  validateStatus: (status) => status == 200,

  /**
   * 对接口响应数据进行处理，返回ture：promise 将被 resolve，否则，promise 将被 reject
   * @param data
   * @returns {boolean}
   */
  validateData: (data) => data.success === true,

  /**
   * 响应失败后的统一处理,只能处理json响应
   * @param res
   */
  handlerDataError: (data) => {},

  /**
   * 请求前对配置参数进行转换
   * @param config
   */
  transformRequestConfig: (config) => config,
};

export class Http {
  constructor(config = {}) {
    this.config = { ...defaultConfig, ...config };
    this._axios = this.createAxios();
    //如果
    this._worker = new Map();
  }

  get(url, params = {}) {
    return this.request({ method: "get", url, params });
  }

  post(url, data = {}) {
    return this.request({ method: "post", url, data });
  }

  put(url, params = {}) {
    return this.request({ method: "put", url, params });
  }

  delete(url, params = {}) {
    return this.request({ method: "delete", url, params });
  }

  postForm(url, data = {}) {
    return this.request({
      method: "post",
      url,
      data,
      headers: {
        "content-type": "application/x-www-form-urlencoded; charset=UTF-8",
      },
    });
  }

  /**
   * 下载文件
   * 注意！！Mock插件会影响下载功能，如果出现下载后文件打不开的情况，把Mock注释掉
   * @param url
   * @param method
   * @param data  post请求的参数
   * @param params get请求的参数
   * @param responseType 默认blob,可用blob/arraybuffer
   * @returns {any | Promise<unknown>}
   */
  download({
    url,
    method = "post",
    data = {},
    params = {},
    responseType = "blob",
  }) {
    return this.request({
      method,
      url,
      data,
      params,
      responseType,
    });
  }

  /**
   * 通用请求方法
   * @param options {
   *                  url:'',
   *                  baseURL:'',
   *                  method:'get',//默认是 get; 'post'、'get'
   *                  params:{},//请求参数在拼接在url上面
   *                  data:{},//请求主体被发送的数据,只适用于这些请求方法 'PUT', 'POST', 和 'PATCH'
   *                  timeout:15 * 1000,// 超时时间默认15s
   *                  responseType:"json",// 表示服务器响应的数据类型，可以是 'arraybuffer', 'blob', 'document', 'json', 'text', 'stream'
   *                  headers:{},
   *                  single:false,//请求去重，如果为true：同时发多个请求，只有一个会发送真实请求，其他放入缓存数组，等响应成功后全部返回
   *              }
   */
  request(options) {
    if (!options) throw new Error("Http:options不能为空！");
    if (!options.url) throw new Error("Http:url不能为空！");

    let newOptions = mergeOptions(options, this.config);
    newOptions.sameKey = getRequestKey(options);
    newOptions.single = Object.prototype.hasOwnProperty.call(options, "single")
      ? options.single
      : false;

    if (!newOptions.single) {
      return this._axios(newOptions);
    }

    //需要去重的请求
    const { sameKey } = newOptions;
    const worker = this._worker;

    return new Promise((resolve, reject) => {
      if (worker.has(sameKey)) {
        worker.get(sameKey).push({ resolve, reject });
      } else {
        worker.set(sameKey, [{ resolve, reject }]);

        this._axios(newOptions)
          .then((res) => {
            worker.get(sameKey).forEach((item) => item.resolve(res));
            worker.delete(sameKey);
          })
          .catch((error) => {
            worker.get(sameKey).forEach((item) => item.reject(error));
            worker.delete(sameKey);
          });
      }
    });
  }

  createAxios() {
    let axiosNew = axios.create({ timeout: 15 * 1000 });
    // 本地支持跨域请求带上cookie；线上环境不支持(如果线上放开了，导致省市区json访问不了)
    if (process.env.NODE_ENV === "development") {
      axiosNew.defaults.withCredentials = true; // 设置为true,跨域请求带上cookie
    }
    //请求拦截
    axiosNew.interceptors.request.use(
      (config) => {
        //是否是表单请求,文件上传形式
        const isFormData =
          Object.prototype.toString.call(config.data) == "[object FormData]";
        const headers = config.headers;

        if (
          !isFormData &&
          (config.method === "post" ||
            config.method === "put" ||
            config.method === "delete") &&
          headers &&
          headers["content-type"] ===
            "application/x-www-form-urlencoded; charset=UTF-8"
        ) {
          // 序列化
          config.data = qs.stringify(config.data);
        }
        if (this.config.debug) {
          console.log("config", config);
        }

        if (this.config.transformRequestConfig) {
          return this.config.transformRequestConfig(config);
        }

        return config;
      },
      (error) =>
        Promise.reject(new HttpError(HttpErrorType.REQUEST, null, null, error))
    );
    //响应拦截
    axiosNew.interceptors.response.use(
      (res) => {
        //判断HTTP响应状态码
        if (!this.config.validateStatus(res.status)) {
          return Promise.reject(
            new HttpError(
              HttpErrorType.SERVICE,
              `Server Error:status=${res.status}`
            )
          );
        }

        const responseType = res.config.responseType;
        if ("blob" === responseType) {
          return this.handlerBlobRes(res);
        } else if ("arraybuffer" === responseType) {
          return this.handlerArraybufferRes(res);
        } else if ("json" === responseType) {
          return this.handlerJsonRes(res);
        } else {
          // 字符串响应、其他响应，以后业务中遇到再完善
          return res.data;
        }
      },
      (error) => {
        if (error.response) {
          //服务器有响应
          return Promise.reject(
            new HttpError(HttpErrorType.SERVICE, null, null, error)
          );
        } else {
          //无网络或者服务器无响应
          return Promise.reject(
            new HttpError(
              HttpErrorType.NOT_NETWORK,
              "Network Error:not network",
              null,
              error
            )
          );
        }
      }
    );

    return axiosNew;
  }

  /**
   * 处理blob响应
   * @param response
   * @returns {Promise<unknown>}
   */
  handlerBlobRes(response) {
    const blob = response.data;
    if (/application\/json/i.test(response.data.type)) {
      // json格式
      return blob2json(blob)
        .catch((str) => {
          response.data = str;
          return this.handlerJsonRes(response);
        })
        .then((json) => {
          response.data = json;
          return this.handlerJsonRes(response);
        });
    }

    // blob流程不直接下载
    if (response.config.params && response.config.params.isNotDownload) {
      return new Promise((resolve, reject) => {
        const contentType = response.headers["content-type"];
        if (/application\/json/i.test(contentType)) {
          reject(
            new HttpError(
              HttpErrorType.API,
              `Api Error:${response.data.errorCode}-${
                response.data.errorMessage || response.data.message
              }`,
              response.data
            )
          );
        }
        // response.data = new Blob([blob], { type: contentType });
        resolve(response);
      });
    }

    return new Promise((resolve, reject) => {
      // 文档下载
      const fileName = getFileName(response.headers);
      downloadFile(response.data, fileName, response.headers["content-type"]);
      resolve(fileName);
    });
  }

  /**
   * 处理arraybuffer响应
   * @param response
   * @returns {Promise<unknown>}
   */
  handlerArraybufferRes(response) {
    const contentType = response.headers["content-type"];
    const options = {};
    if (/application\/json/i.test(contentType)) {
      options.type = contentType;
    }
    response.data = new Blob([response.data], { type: contentType });
    return this.handlerBlobRes(response);
  }

  /**
   * 处理json格式响应
   * @param response
   */
  handlerJsonRes(res) {
    if (typeof res.data === "string") {
      //json解析报错
      return Promise.reject(
        new HttpError(
          HttpErrorType.API_JSON_ERROR,
          `Api Error:响应数据json解析错误`,
          res.data
        )
      );
    }

    // 将接口返回的success为string改为boolean类型
    if (typeof res.data.success === "string") {
      res.data.success = res.data.success === "true";
    }

    if (this.config.validateData(res.data)) {
      return Promise.resolve(res.data);
    }
    res.data.config = res.config;
    this.config.handlerDataError(res.data);

    return Promise.reject(
      new HttpError(
        HttpErrorType.API,
        `Api Error:${res.data.errorCode}-${
          res.data.errorMessage || res.data.message
        }`,
        res.data
      )
    );
  }
}

/**
 * 请求的key，url和参数相同，返回同一个值
 * @param options
 * @returns {string}
 */
function getRequestKey(options) {
  let { url, params, data } = options;
  let query = { ...params, ...data };
  let querySort = {};
  Object.keys(query)
    .sort()
    .forEach((key) => (querySort[key] = query[key]));
  return url + "@@" + JSON.stringify(querySort);
}

function mergeOptions(options = {}, config = {}) {
  return {
    ...options, // 业务侧层传入自定义配置项
    url: options.url || "",
    baseURL: options.baseURL || config.baseURL,
    method: options.method || "get",
    params: options.params || null,
    data: options.data || null,
    timeout: options.timeout || config.timeout,
    headers: { ...config.headers, ...options.headers },
    responseType: options.responseType || config.responseType,
  };
}

export const HttpErrorType = {
  NOT_NETWORK: -1, //无网络,服务器无响应
  API: -4, //接口错误:success!==true
  API_JSON_ERROR: -6, //json数据解析失败
  SERVICE: -2, //服务器错误，请求错误返回码不为：200
  REQUEST: -5, //请求之前错误，
};

export class HttpError extends Error {
  /**
   *
   * @param type // 错误类型, HttpErrorType中的值
   * @param message
   * @param data //接口响应的数据
   * @param error 可为空
   */
  constructor(type, message, data, error) {
    super(message);
    this.type = type;
    this.data = data;
    if (!message && error) {
      this.message = error.message;
    }
    if (error) {
      this.stack = error.stack;
    }
  }
}

/**
 * 判断错误的类型
 * @param error
 * @param httpErrorType
 * @returns {boolean}
 */
HttpError.isType = function (error, httpErrorType) {
  return error && error instanceof HttpError && error.type == httpErrorType;
};
/**
 * 是否是接口错误
 * @param error
 * @returns {boolean}
 */
HttpError.isApiType = function (error) {
  return HttpError.isType(error, HttpErrorType.API);
};

/**
 * 判断接口返回的errorCode是否在errorCodes中
 * @param error
 * @param errorCodes 可以传 string 或 array
 */
HttpError.hasErrorCode = (error, errorCodeList = []) => {
  if (!error || !errorCodeList || !HttpError.isApiType(error)) return false;

  const errorCode = error.data.errorCode;

  if (typeof errorCodeList === "string") {
    return errorCode == errorCodeList;
  } else if (Array.isArray(errorCodeList)) {
    return errorCodeList.some((item) => item == errorCode);
  }
  return false;
};

/**
 * 获取errorMessage
 * @param error
 * @returns {*|string}
 */
HttpError.getMsg = function (error) {
  return HttpError.isType(error, HttpErrorType.API)
    ? error.data.errorMessage
    : "请求失败，请稍后重试";
};

/**
 * 跨域请求封装的jsonp,只能get请求
 * @param url
 * @param data
 * @returns {Promise<unknown>}
 */
export const jsonp = (url, data = {}) => {
  return new Promise((resolve, reject) => {
    window.__fn__ = (data) => {
      resolve(data);
    };
    let script = document.createElement("script");
    let query = Object.entries(data)
      .map((a) => `${a[0]}=${a[1]}`)
      .join("&");
    script.src = url + "?callback=__fn__&" + query;
    script.onerror = () => reject("加载失败");
    document.head.appendChild(script);
    document.head.removeChild(script);
  });
};

function getFileName(headers) {
  const str = decodeURIComponent(headers["content-disposition"]);
  return str.split("filename=")[1];
}

/**
 * 下载文件流
 * @param data
 * @param name
 * @param type
 */
function downloadFile(data, name, type) {
  const options = {};
  if (type) {
    options.type = type;
  }
  let blob = new Blob([data], options);
  saveAs(blob, name);
}

function blob2json(blob) {
  return new Promise((resolve, reject) => {
    let reader = new FileReader(); // 创建读取文件对象
    reader.onload = function (event) {
      try {
        let res = JSON.parse(reader.result);
        resolve(res);
      } catch (e) {
        //json解析报错
        reject(reader.result);
      }
    };
    reader.readAsText(blob, "utf-8"); // 设置读取的数据以及返回的数据类型为utf-8
  });
}

export const http = new Http();
